import { AgGridReact } from 'ag-grid-react';
import { FC, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmationModal from '../../common/DeleteConfirmationModal/DeleteConfirmationModal';
import Header from '../../common/Header/Header';
import {
  Buttons,
  Page,
  TableWrapper,
} from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import Button from '../../components/Button/Button';
import { Variant } from '../../components/Button/Button.types';
import Table from '../../components/Table/Table';
import { showToast } from '../../components/Toast/Toast';
import { useClassBases, useDeleteClassBase } from '../../services/base/base.service';
import { ClassBase } from '../../services/base/base.types';
import { useFetchErrorToast } from '../../utils/useFetchErrorToasts/useFetchErrorToast';
import { genericErrorShowToastOptions } from '../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../utils/useNavigateWithToast/useNavigateWithToast';
import { CSVLink } from 'react-csv';
import Divider from '@mui/material/Divider';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { ValueGetterParams } from 'ag-grid-community';

const ClassBases: FC = () => {
  const navigate = useNavigate();
  const navigateWithToast = useNavigateWithToast();
  const gridRef = useRef<AgGridReact>(null);
  const [selectedRow, setSelectedRow] = useState<ClassBase | undefined>(undefined);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const muiTheme = createTheme();

  const { data, isError: classBasesError, refetch } = useClassBases({}, true);
  const allClassBases = data?.data || [];

  const { mutate, isLoading: isDeleteLoading } = useDeleteClassBase();

  const headers = [
    { label: 'Class Number', key: 'classNo' },
    { label: 'Base Number', key: 'baseNo' },
    { label: 'Class Description', key: 'classDescription' },
    { label: 'Class Status Number', key: 'classStatusNo' },
    { label: 'PCA Class Number', key: 'pcaClassNo' },
    { label: 'Price Option Number', key: 'priceOptionNo' },
    { label: 'Print In Kitchen', key: 'printInKitchen' },
    { label: 'Allow Discount', key: 'allowDiscount' },
    { label: 'Tax Exempted', key: 'taxExempted' },
    { label: 'Allow Half', key: 'allowHalf' },
    { label: 'Class Mnemonic', key: 'classMnemonic' },
    { label: 'Class Sequence Number', key: 'classSeqNo' },
    { label: 'Class Long Description', key: 'classLongDescription' },
    { label: 'Class Valid On Web', key: 'classValidOnWeb' },
    { label: 'Class Display Description', key: 'classDisplayDesc' },
    { label: 'Base Description', key: 'baseDescription' },
    { label: 'Base Status Number', key: 'baseStatusNo' },
    { label: 'Base Mnemonic', key: 'baseMnemonic' },
    { label: 'Base Sequence Number', key: 'baseSeqNo' },
    { label: 'Base Long Description', key: 'baseLongDescription' },
    { label: 'Synopsis', key: 'synopsis' },
    { label: 'Base Valid On Web', key: 'baseValidOnWeb' },
    { label: 'Display In OE', key: 'displayInOe' },
    { label: 'Base Display Description', key: 'baseDisplayDesc' },
    { label: 'Created By', key: 'createdBy' },
    { label: 'Created Date', key: 'createdDate' },
    { label: 'Updated By', key: 'updatedBy' },
    { label: 'Updated Date', key: 'updatedDate' },
  ];

  const csvData = allClassBases.map((classBase: ClassBase) => ({
    classNo: classBase.classNo,
    baseNo: classBase.baseNo,
    classDescription: classBase.classData.description,
    classStatusNo: classBase.classData.statusNo,
    pcaClassNo: classBase.classData.pcaClassNo,
    priceOptionNo: classBase.classData.priceOptionNo,
    printInKitchen: classBase.classData.printInKitchen ? 'Y' : 'N',
    allowDiscount: classBase.classData.allowDiscount ? 'Y' : 'N',
    taxExempted: classBase.classData.taxExempted ? 'Y' : 'N',
    allowHalf: classBase.classData.allowHalf ? 'Y' : 'N',
    classMnemonic: classBase.classData.mnemonic,
    classSeqNo: classBase.classData.seqNo,
    classLongDescription: classBase.classData.longDescription,
    classValidOnWeb: classBase.classData.validOnWeb ? 'Y' : 'N',
    classDisplayDesc: classBase.classData.displayDesc,
    baseDescription: classBase.baseData.description,
    baseStatusNo: classBase.baseData.statusNo,
    baseMnemonic: classBase.baseData.mnemonic,
    baseSeqNo: classBase.baseData.seqNo,
    baseLongDescription: classBase.baseData.longDescription,
    synopsis: classBase.baseData.synopsis,
    baseValidOnWeb: classBase.baseData.validOnWeb ? 'Y' : 'N',
    displayInOe: classBase.baseData.displayInOe ? 'Y' : 'N',
    baseDisplayDesc: classBase.baseData.displayDesc,
    createdBy: classBase.createdBy,
    createdDate: new Date(classBase.createdDate).toISOString(),
    updatedBy: classBase.updatedBy,
    updatedDate: new Date(classBase.updatedDate).toISOString(),
  }));

  const handleRowSelection = () => {
    const selectedRows = gridRef?.current?.api.getSelectedRows();
    setSelectedRow(selectedRows?.[0]);
  };

  const handleAddNew = () => {
    navigate(`/tables/classbases/addClassBase`);
  };

  const handleDelete = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    mutate(
      {
        classNo: selectedRow?.classNo,
        baseNo: selectedRow?.baseNo,
      },
      {
        onSuccess: () => {
          navigateWithToast(`/tables/classbases`, {
            label: 'ClassBase has been deleted',
            id: 'classBaseDeleteSuccess',
          });
          refetch();
          closeDeleteModal();
          setSelectedRow(undefined);
        },
        onError: () => {
          showToast(genericErrorShowToastOptions);
          closeDeleteModal();
        },
      }
    );
  };

  const openDeleteModal = () => {
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  useFetchErrorToast([{ isError: classBasesError, label: 'Error fetching ClassBases', id: 'classBasesFetchError' }]);

  const handleOpenClick = () => {
    navigate(`/tables/classbases/${selectedRow?.classNo}/${selectedRow?.baseNo}`);
  };

  return (
    <>
      <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='ClassBase' />
      <Page>
        <TableWrapper>
          <Table
            rowData={data?.data}
            ref={gridRef}
            returnedRecords={data?.data?.length}
            height={'100%'}
            rowSelection='single'
            onRowSelected={handleRowSelection}
            columnDefs={[
              {
                maxWidth: 48,
                field: '',
                headerName: '',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
              },
              { sortable: true, minWidth: 80, field: 'classData.classNo', sort: 'asc', headerName: 'MOA Class Number' },
              { minWidth: 80, field: 'classData.description', headerName: 'MOA Class Description' },
              { minWidth: 80, field: 'baseNo', headerName: 'MOA Base Number' },
              { minWidth: 80, field: 'baseData.description', headerName: 'MOA Base Description' },
              {
                minWidth: 80,
                field: 'classData.imageId',
                headerName: 'GUI Image',
                valueFormatter: (params) => (params.value ? 'Y' : 'N'),
                filterValueGetter: (params: ValueGetterParams) => (params.getValue('imageId') ? 'Yes' : 'No'),
              },
            ]}
          />
        </TableWrapper>
        <Buttons>
          <Button
            label='Open'
            variant={Variant.Primary}
            onClick={handleOpenClick}
            disabled={selectedRow === undefined}
          />
          <Button
            testId='addNewClassBaseActionButton'
            label='Add New'
            variant={Variant.Secondary}
            onClick={handleAddNew}
          />
          <Button
            label='Delete'
            testId='openDeleteModalConfirmationActionButton'
            variant={Variant.Secondary}
            onClick={openDeleteModal}
            disabled={selectedRow === undefined}
          />
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <Divider orientation='horizontal' variant={'fullWidth'} flexItem />
          </ThemeProvider>
          <CSVLink
            data={csvData}
            headers={headers}
            filename={`classbases_${new Date().toISOString().split('T')[0]}.csv`}
            target='_blank'
            className='btn btn-primary'
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Button label='Export to CSV' variant={Variant.Secondary} />
          </CSVLink>
        </Buttons>
        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
          cancelBtnTestId='cancelDeleteClassBaseModalButton'
          confirmBtnTestId='confirmDeleteClassBaseModalButton'
          title={'Delete ClassBase Record'}
          message={'Are you sure you want to delete this ClassBase?'}
          isLoading={isDeleteLoading}
        />
      </Page>
    </>
  );
};

export default ClassBases;
